import React, { useEffect, useState } from 'react';
import { apiFetch } from './api';
import './Auth.css';

const UserAdmin = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetUsername, setResetUsername] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadUsers = async () => {
    const response = await apiFetch('/api/admin/users');
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Unable to load users');
    const loadedUsers = body.users || [];
    setUsers(loadedUsers);
    setResetUsername((current) => current
      || loadedUsers.find((user) => user.username === 'edward')?.username
      || loadedUsers[0]?.username
      || '');
  };

  useEffect(() => {
    loadUsers().catch((requestError) => setError(requestError.message));
  }, []);

  const run = async (request, successMessage) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await request();
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Request failed');
      setMessage(successMessage);
      await loadUsers();
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const createUser = async (event) => {
    event.preventDefault();
    const username = newUsername.trim().toLowerCase();
    const created = await run(
      () => apiFetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: newPassword }),
      }),
      `${username} created`
    );
    if (created) {
      setNewUsername('');
      setNewPassword('');
      setResetUsername(username);
    }
  };

  const submitPasswordReset = async (event) => {
    event.preventDefault();
    const username = resetUsername.trim().toLowerCase();
    const reset = await run(
      () => apiFetch(`/api/admin/users/${encodeURIComponent(username)}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: resetPasswordValue }),
      }),
      `${username}'s password reset`
    );
    if (reset) setResetPasswordValue('');
  };

  // Chart defaults per user. Each toggle sends only the flag it owns, so the
  // other is left untouched by the backend rather than being reset.
  const setChartPref = async (username, field, value) => {
    await run(
      () => apiFetch(`/api/admin/users/${encodeURIComponent(username)}/chart-prefs`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      }),
      `${username}: chart preference saved`
    );
    await loadUsers();
  };

  const setLiveAccess = async (username, enabled) => {
    await run(
      () => apiFetch(`/api/admin/users/${encodeURIComponent(username)}/live-access`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      }),
      `${username}: live trading ${enabled ? 'enabled' : 'disabled'}`
    );
    await loadUsers();
  };

  return (
    <div className="admin-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="admin-dialog" role="dialog" aria-modal="true" aria-label="User administration">
        <header>
          <div><strong>Users</strong><span>Edward administrator</span></div>
          <button type="button" onClick={onClose} aria-label="Close user administration">×</button>
        </header>

        <form onSubmit={createUser}>
          <h2>Create user</h2>
          <input
            placeholder="Username"
            value={newUsername}
            maxLength="40"
            onChange={(event) => setNewUsername(event.target.value.toLowerCase())}
          />
          <input
            type="password"
            placeholder="Temporary password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <button type="submit" disabled={busy || !newUsername || !newPassword}>Create</button>
        </form>

        <form onSubmit={submitPasswordReset}>
          <h2>Reset password</h2>
          <select value={resetUsername} onChange={(event) => setResetUsername(event.target.value)}>
            {users.map((user) => <option key={user.username} value={user.username}>{user.username}</option>)}
          </select>
          <input
            type="password"
            placeholder="New password"
            autoComplete="new-password"
            value={resetPasswordValue}
            onChange={(event) => setResetPasswordValue(event.target.value)}
          />
          <button type="submit" disabled={busy || !resetUsername || !resetPasswordValue}>Reset</button>
        </form>

        <section className="admin-chart-prefs">
          <h2>Per-user settings</h2>
          <p className="password-rule">
            Which chart a user starts on: they can switch at any time, and that
            choice then sticks for every chart - trading and review alike. Live
            trading is off until granted, so a new user only ever sees Train.
          </p>
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th title="Which chart a user starts on before they touch the toggle">Default chart: TV</th>
                <th title="Off means the user only ever sees Train. The administrator always has live access.">Live trading</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.username}>
                  <td>{user.username}</td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`TradingView chart for trading for ${user.username}`}
                      disabled={busy}
                      checked={user.trading_chart_tv !== false}
                      onChange={(event) => setChartPref(user.username, 'tradingChartTv', event.target.checked)}
                    />
                  </td>
                  <td>
                    {user.is_admin ? (
                      // The admin's access is not a setting, so there is no
                      // control here that could imply it can be switched off.
                      <span className="admin-always" title="The administrator always has live access">always</span>
                    ) : (
                      <input
                        type="checkbox"
                        aria-label={`Live trading for ${user.username}`}
                        disabled={busy}
                        checked={user.live_access === true}
                        onChange={(event) => setLiveAccess(user.username, event.target.checked)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <p className="password-rule">At least 8 characters with one special character.</p>
        {message && <div className="auth-message">{message}</div>}
        {error && <div className="auth-error">{error}</div>}
      </section>
    </div>
  );
};

export default UserAdmin;
